import { getPacienteUnificado, savePacienteUnificado } from '@/services/monolito';

export default {
  data() {
    return {
      pacienteLoading: false,
      pacienteError: null,
      pacienteData: null,
      pacienteTipoConsulta: null
    };
  },

  methods: {
    /**
     * Consulta un paciente usando el criterio más apropiado
     * @param {string|null} criterio - Email, DNI, ID o null para paginación
     * @param {object} params - Parámetros adicionales para paginación
     * @returns {Promise<object>} Resultado de la consulta
     */
    async consultarPaciente(criterio = null, params = {}) {
      this.pacienteLoading = true;
      this.pacienteError = null;

      try {


        // Determinar el criterio más apropiado
        let criterioFinal = criterio;

        // Si no se proporciona criterio, intentar usar el del store
        if (!criterioFinal && this.$store.state.credentials) {
          const credentials = this.$store.state.credentials;

          // Prioridad: email > dni > null
          if (credentials.fedacct && credentials.fedacct.includes('@')) {
            criterioFinal = credentials.fedacct;
          } else if (credentials.dni) {
            criterioFinal = credentials.dni;
          }
        }

        const resultado = await getPacienteUnificado(criterioFinal, params);

        if (resultado.success) {
          this.pacienteData = resultado.data;
          this.pacienteTipoConsulta = resultado.tipoConsulta;
          return resultado;
        } else {
          this.pacienteError = resultado.error;
          console.error('Error consultando paciente:', resultado.error);
          return resultado;
        }

      } catch (error) {
        this.pacienteError = error.message;
        console.error('Error en consultarPaciente:', error);
        return {
          success: false,
          error: error.message,
          data: null
        };
      } finally {
        this.pacienteLoading = false;
      }
    },

    /**
     * Guarda o actualiza un paciente
     * @param {object} pacienteData - Datos del paciente
     * @param {string} operacion - 'create' o 'update'
     * @returns {Promise<object>} Resultado de la operación
     */
    async guardarPaciente(pacienteData, operacion = 'update') {
      this.pacienteLoading = true;
      this.pacienteError = null;

      try {
        const resultado = await savePacienteUnificado(pacienteData, operacion);

        if (resultado.success) {
          return resultado;
        } else {
          this.pacienteError = resultado.error;
          console.error('Error guardando paciente:', resultado.error);
          return resultado;
        }

      } catch (error) {
        this.pacienteError = error.message;
        console.error('Error en guardarPaciente:', error);
        return {
          success: false,
          error: error.message
        };
      } finally {
        this.pacienteLoading = false;
      }
    },

    /**
     * Procesa los datos del paciente según el tipo de respuesta
     * @param {object} data - Datos del paciente
     * @param {string} tipoConsulta - Tipo de consulta realizada
     * @returns {object} Datos procesados
     */
    procesarDatosPaciente(data, tipoConsulta) {
      if (!data) return null;

      // Si los datos vienen en formato array (aplica para id, dni, email, etc.)
      if (Array.isArray(data)) {
        let [paciente, direccion, contacto, usuario] = data;

        // Extract specific contact if it's an array
        if (Array.isArray(contacto)) {
          const mobile = contacto.find(c => c.type === 'mobile');
          const phone = contacto.find(c => c.type === 'phone');
          contacto = mobile || phone || contacto[0] || {};
        }

        return {
          paciente: paciente,
          direccion: direccion || {},
          contacto: contacto || {},
          usuario: usuario || {},
          tipoConsulta: tipoConsulta
        };
      }

      // Para otros tipos de consulta, devolver los datos tal como vienen
      let contacto = data.contacto || (data.party && data.party.contacto) || {};
      let direccion = (data.direcciones && data.direcciones.length > 0) ? data.direcciones[0] :
        (data.direccion || (data.party && data.party.direcciones && data.party.direcciones.length > 0 ? data.party.direcciones[0] : {}));

      // Extract specific contact if it's an array
      if (Array.isArray(contacto)) {
        const mobile = contacto.find(c => c.type === 'mobile');
        const phone = contacto.find(c => c.type === 'phone');
        contacto = mobile || phone || contacto[0] || {};
      }

      return {
        paciente: data,
        direccion: direccion,
        contacto: contacto,
        tipoConsulta: tipoConsulta
      };
    },

    /**
     * Determina el criterio de consulta más apropiado
     * @param {object} paciente - Datos del paciente
     * @returns {string|null} Criterio de consulta
     */
    determinarCriterioConsulta(paciente) {
      if (!paciente) return null;

      // Prioridad: email > dni > id
      if (paciente.email && paciente.email.includes('@')) {
        return paciente.email;
      }

      if (paciente.dni) {
        return paciente.dni;
      }

      if (paciente.id) {
        return paciente.id;
      }

      return null;
    },

    /**
     * Valida que los datos del paciente sean completos
     * @param {object} pacienteData - Datos del paciente
     * @returns {object} Resultado de la validación
     */
    validarDatosPaciente(pacienteData) {
      const errores = [];

      if (!pacienteData.name) {
        errores.push('El nombre es requerido');
      }

      if (!pacienteData.lastname) {
        errores.push('El apellido es requerido');
      }

      if (!pacienteData.dni) {
        errores.push('El DNI es requerido');
      }

      if (!pacienteData.telefono) {
        errores.push('El teléfono es requerido');
      }

      return {
        valido: errores.length === 0,
        errores: errores
      };
    }
  }
}; 